import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import {
  DATE_FORMAT,
  LoadingEvent,
  useLocalization,
} from '@ui-framework/index';
import {
  IconLibrary,
  useAvailabilitySlotTypes,
} from '@product-framework/index';
import PermissionButton from '@product-framework/components/PermissionButton';
import PageSection from '@product-framework/components/PageSection';
import FormErrorsArray from '@ui-framework/components/AdvancedForms/FormErrorsArray';

import {
  CURRENT_VIEW,
  WEEKDAY_KEYS,
  WEEKDAY_SHORT_LABELS,
} from '../../utils/constants';
import DefaultSchedule from '../../utils/DefaultSchedule';
import lib from '../../utils/lib';

function DefaultScheduleDetailView({
  params,
  currentView,
  setCurrentView,
  selectedScheduleId,
  setSelectedScheduleId,
  locationId = null,
}) {
  const { formatMessage } = useLocalization();
  const { slotTypes } = useAvailabilitySlotTypes();
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);

  const initialize = async () => {
    setLoading(true);
    setErrors(false);
    setCurrentSchedule(null);

    const response = await DefaultSchedule.fetch({
      params,
      defaultScheduleId: selectedScheduleId,
      locationId,
    });

    if (response?.ok) {
      const processedData = DefaultSchedule.parseSuccessResponseForNextValue({
        schedule: response?.data,
        slotTypes,
        serviceProviderId: params?.pageId,
      });

      setCurrentSchedule(processedData);
    } else {
      setErrors(response?.errors);
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [selectedScheduleId, currentView]);

  let componentLoadingOrError = null;

  if (loading) {
    componentLoadingOrError = (
      <LoadingEvent>Loading Default Schedule</LoadingEvent>
    );
  }

  if (errors) {
    componentLoadingOrError = (
      <FormErrorsArray
        errors={errors}
        withTitle={false}
        displayAsList={false}
      />
    );
  }

  // 100% / 8 columns
  const width = '12%';

  return (
    <PageSection
      id="ScrollTopElementDefaultScheduleView"
      subSection
      label={formatMessage({
        id: 'VIEW_SCHEDULE',
        defaultMessage: 'View Schedule',
      })}>
      {componentLoadingOrError || (
        <Box sx={{ minHeight: '310px' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
              <FormattedMessage
                id="EFFECTIVE START DATE"
                defaultMessage="Effective Start Date"
              />
              :
            </Typography>
            <Typography variant="body1">
              {dayjs(currentSchedule?.effectiveStartDate).format(
                DATE_FORMAT.FRIENDLY_WEEKDAY_DATE,
              )}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
              <FormattedMessage
                id="EFFECTIVE END DATE"
                defaultMessage="Effective End Date"
              />
              :
            </Typography>
            <Typography variant="body1">
              {currentSchedule?.effectiveEndDate ? (
                dayjs(currentSchedule?.effectiveEndDate).format(
                  DATE_FORMAT.FRIENDLY_WEEKDAY_DATE,
                )
              ) : (
                <FormattedMessage id="INDEFINITE" defaultMessage="Indefinite" />
              )}
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ flexShrink: 0, mb: 1 }}>
            <FormattedMessage id="SCHEDULE" defaultMessage="Schedule" />:
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  '& .MuiTableCell-root': { border: 'none', py: 1.25, px: 1 },
                }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {WEEKDAY_KEYS.map(wk => (
                      <TableCell key={wk} width={width} align="center">
                        <Typography variant="subtitle2">
                          {WEEKDAY_SHORT_LABELS[wk]}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slotTypes.map(sk => (
                    <TableRow key={sk?.id}>
                      <TableCell align="right">
                        <Typography variant="subtitle2">{sk?.name}</Typography>
                      </TableCell>
                      {WEEKDAY_KEYS.map(wk => {
                        const slotOn = Boolean(
                          currentSchedule?.schedule[wk]?.[sk?.code],
                        );
                        let slotContent;
                        if (slotOn) {
                          slotContent = (
                            <CheckCircle
                              fontSize="small"
                              sx={{
                                color: 'success.main',
                                verticalAlign: 'middle',
                              }}
                            />
                          );
                        } else {
                          slotContent = (
                            <Cancel
                              fontSize="small"
                              sx={{
                                color: 'error.main',
                                verticalAlign: 'middle',
                              }}
                            />
                          );
                        }
                        return (
                          <TableCell key={`${wk}-${sk?.code}`} align="center">
                            {slotContent}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        <FormattedMessage
                          id="CUTOFF_TIME"
                          defaultMessage="Cutoff Time"
                        />
                      </Typography>
                    </TableCell>
                    {WEEKDAY_KEYS.map(wk => (
                      <TableCell key={`${wk}-cutoff`} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {currentSchedule.schedule[wk]?.cutoff?.time
                            ? lib.formatCutoffSummary(
                                wk,
                                currentSchedule.schedule[wk]?.cutoff,
                              )
                            : ''}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: 'center', mt: 2 }}>
              <PermissionButton
                permissionName="CAN_UPDATE_SP_AVAILABILITY_DEFAULT_SCHEDULE"
                size="small"
                variant="contained"
                onClick={() => {
                  setSelectedScheduleId(selectedScheduleId);
                  setCurrentView(CURRENT_VIEW.EDIT_SCHEDULE);
                }}>
                <FormattedMessage
                  id="EDIT_SCHEDULE"
                  defaultMessage="Edit Schedule"
                />
              </PermissionButton>
              <Button
                sx={{ mt: 2, mb: 2 }}
                variant="outlined"
                color="primary"
                startIcon={<IconLibrary.Back />}
                onClick={() => {
                  setSelectedScheduleId(null);
                  setCurrentView(CURRENT_VIEW.DEFAULT);
                }}>
                <FormattedMessage
                  id="BACK_TO_LIST"
                  defaultMessage="Back to List"
                />
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </PageSection>
  );
}

export default DefaultScheduleDetailView;
